import { useState } from 'react';
import { Card } from '../../../layout/Card.jsx';
import { KPI } from '../../../layout/KPI.jsx';
import { formatIQD, formatDate } from '../../../shared/format.js';
import { useSubscriptionsReport } from '../hooks/useSubscriptions.js';

function monthStart() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export function SubscriptionsReport() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const { data, isLoading, error } = useSubscriptionsReport({
    from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined
  });

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">من</label>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">إلى</label>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {error ? (
        <Card><div className="text-red text-center py-4">تعذّر تحميل التقرير</div></Card>
      ) : isLoading || !data ? (
        <Card><div className="text-ink-3 text-center py-8">جاري التحميل...</div></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI
              label="إيراد الاشتراكات في المدى"
              value={formatIQD(data.soldTotal?.amount || 0)}
              delta={`${data.soldTotal?.count || 0} عملية (بيع + تجديد)`}
              icon="finance"
              tone="orange"
            />
            <KPI label="اشتراكات فعّالة" value={data.byStatus?.active || 0} icon="card" tone="green" />
            <KPI
              label="تنتهي خلال 7 أيام"
              value={data.expiringSoon || 0}
              icon="clock"
              tone="yellow"
            />
            <KPI
              label="منتهية / ملغاة"
              value={`${data.byStatus?.expired || 0} / ${data.byStatus?.cancelled || 0}`}
              icon="reports"
              tone="purple"
            />
          </div>

          <Card
            header="تفصيل المدى"
            sub={`${formatDate(data.period?.from)} ← ${formatDate(data.period?.to)}`}
          >
            <div className="grid grid-cols-2 gap-3 text-[12.5px]">
              <div className="rounded-[10px] border border-line p-3">
                <div className="text-ink-3">اشتراكات جديدة</div>
                <div className="text-[17px] font-extrabold mt-0.5">{data.sold?.new?.count || 0}</div>
                <div className="text-ink-2">{formatIQD(data.sold?.new?.amount || 0)}</div>
              </div>
              <div className="rounded-[10px] border border-line p-3">
                <div className="text-ink-3">تجديدات</div>
                <div className="text-[17px] font-extrabold mt-0.5">{data.sold?.renewal?.count || 0}</div>
                <div className="text-ink-2">{formatIQD(data.sold?.renewal?.amount || 0)}</div>
              </div>
            </div>
          </Card>

          <Card header="حسب الباقة" sub="المبيع والمجدَّد في المدى المحدد">
            {(data.byPlan || []).length === 0 ? (
              <div className="text-ink-3 text-center py-6 text-[13px]">لا مبيعات اشتراك في هذا المدى</div>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="bg-paper-2 text-ink-2">
                    <th className="text-right font-semibold px-3 py-2">الباقة</th>
                    <th className="text-right font-semibold px-3 py-2">العدد</th>
                    <th className="text-right font-semibold px-3 py-2">المبلغ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.byPlan.map((r) => (
                    <tr key={String(r.planId || r.planName)}>
                      <td className="px-3 py-2 font-semibold">{r.planName}</td>
                      <td className="px-3 py-2">{r.count}</td>
                      <td className="px-3 py-2">{formatIQD(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <div className="text-[11.5px] text-ink-3">
            إيراد الاشتراكات يُسجَّل كفاتورة مبيعات مدفوعة، فيظهر أيضاً في تقارير الإيراد
            العامة ضمن تصنيف «يدوي» وفي نقد الشفت الذي استُلم فيه.
          </div>
        </>
      )}
    </div>
  );
}
